import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Shield,
  MapPin,
  Clock,
  Navigation,
  AlertTriangle,
  CheckCircle,
  User,
  Loader2,
  Phone,
  RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker for tracked user
const userIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40">
      <circle cx="20" cy="20" r="18" fill="#FF3366" stroke="white" stroke-width="3"/>
      <circle cx="20" cy="20" r="8" fill="white"/>
      <circle cx="20" cy="20" r="4" fill="#FF3366"/>
    </svg>
  `),
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20],
});

// SOS marker
const sosIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40">
      <circle cx="20" cy="20" r="18" fill="#FF3B30" stroke="white" stroke-width="3">
        <animate attributeName="r" values="16;18;16" dur="1s" repeatCount="indefinite"/>
      </circle>
      <text x="20" y="26" text-anchor="middle" fill="white" font-size="16" font-weight="bold">!</text>
    </svg>
  `),
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20],
});

// Component to fit bounds
const FitBounds = ({ locations }) => {
  const map = useMap();
  
  useEffect(() => {
    if (locations && locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(l => [l.lat, l.lng]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map]);
  
  return null;
};

const TrackingPage = () => {
  const { shareToken } = useParams();
  const [tripData, setTripData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    let isMounted = true;
    let intervalId = null;
    
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/track/${shareToken}`);
        if (isMounted) {
          setTripData(response.data);
          setLastUpdate(new Date());
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.response?.status === 404 ? "Trajet non trouvé" : "Erreur de chargement");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    // Initial fetch
    fetchData();
    
    // Set up polling for active trips
    intervalId = setInterval(() => {
      // We need to check the current status inside the interval
      // Since we can't depend on tripData here, we'll refetch each time
      fetchData();
    }, 30000);
    
    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [shareToken]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/track/${shareToken}`);
      setTripData(response.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.response?.status === 404 ? "Trajet non trouvé" : "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = () => {
    if (!tripData) return null;
    
    switch (tripData.status) {
      case "active":
        return {
          color: "bg-green-500",
          bgColor: "bg-green-50",
          borderColor: "border-green-500",
          textColor: "text-green-700",
          icon: Navigation,
          label: "En cours",
          description: "Le trajet est en cours"
        };
      case "completed":
        return {
          color: "bg-blue-500",
          bgColor: "bg-blue-50",
          borderColor: "border-blue-500",
          textColor: "text-blue-700",
          icon: CheckCircle,
          label: "Terminé",
          description: "Bien arrivé(e) !"
        };
      case "sos":
        return {
          color: "bg-red-500",
          bgColor: "bg-red-50",
          borderColor: "border-red-500",
          textColor: "text-red-700",
          icon: AlertTriangle,
          label: "ALERTE SOS",
          description: "Une alerte a été déclenchée !"
        };
      default:
        return null;
    }
  };

  const getTimeInfo = () => {
    if (!tripData) return null;
    
    const start = new Date(tripData.started_at);
    const now = new Date();
    const elapsed = Math.floor((now - start) / 60000);
    
    if (tripData.status === "active") {
      const end = new Date(tripData.estimated_end);
      const remaining = Math.floor((end - now) / 60000);
      return {
        elapsed: `${elapsed} min écoulées`,
        remaining: remaining > 0 ? `${remaining} min restantes` : "Temps dépassé"
      };
    }
    
    return {
      elapsed: `Durée: ${elapsed} min`,
      remaining: null
    };
  };

  const getLastPosition = () => {
    if (!tripData?.locations || tripData.locations.length === 0) return null;
    return tripData.locations[tripData.locations.length - 1];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF0F5] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-[#FF3366] mx-auto" size={48} />
          <p className="mt-4 text-slate-600">Chargement du trajet...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFF0F5] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="mx-auto text-amber-500 mb-4" size={48} />
            <h2 className="text-xl font-bold text-slate-900 mb-2">{error}</h2>
            <p className="text-slate-500">
              Ce lien de suivi n'est plus valide ou a expiré.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusInfo();
  const timeInfo = getTimeInfo();
  const lastPosition = getLastPosition();

  return (
    <div className="min-h-screen bg-[#FFF0F5]">
      {/* Header */}
      <header className="bg-white border-b border-pink-100 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-200 to-pink-100 flex items-center justify-center">
                <Shield size={20} className="text-[#FF3366]" fill="#FF3366" />
              </div>
              <div>
                <span className="font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>RATE<span className="text-amber-400">⭐</span>SHARE</span>
                <span className="text-xs text-slate-500 block">Safe Check</span>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleRefresh}
              className="rounded-full"
            >
              <RefreshCw size={14} className="mr-1" />
              Actualiser
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Status Card */}
        <Card className={`mb-6 ${statusInfo?.borderColor} border-2 ${statusInfo?.bgColor}`}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-full ${statusInfo?.color} flex items-center justify-center`}>
                {statusInfo?.icon && <statusInfo.icon className="text-white" size={32} />}
              </div>
              <div>
                <Badge className={`${statusInfo?.color} text-white mb-2`}>
                  {statusInfo?.label}
                </Badge>
                <h2 className="text-xl font-bold text-slate-900">{tripData?.user_name}</h2>
                <p className={statusInfo?.textColor}>{statusInfo?.description}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SOS Alert */}
        {tripData?.status === "sos" && (
          <Card className="mb-6 bg-red-500 text-white border-none">
            <CardContent className="p-6 text-center">
              <AlertTriangle className="mx-auto mb-4 animate-pulse" size={48} />
              <h3 className="text-2xl font-bold mb-2">ALERTE SOS DÉCLENCHÉE</h3>
              <p className="mb-4">
                {tripData.user_name} a déclenché une alerte d'urgence !
              </p>
              <p className="text-lg">
                Contactez-le/la immédiatement ou appelez les secours.
              </p>
              <div className="mt-4 flex justify-center gap-4">
                <a href="tel:15" className="bg-white text-red-500 px-6 py-3 rounded-full font-bold inline-flex items-center">
                  <Phone size={20} className="mr-2" />
                  SAMU (15)
                </a>
                <a href="tel:17" className="bg-white text-red-500 px-6 py-3 rounded-full font-bold inline-flex items-center">
                  <Phone size={20} className="mr-2" />
                  Police (17)
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Map */}
        {lastPosition && (
          <Card className="mb-6 overflow-hidden">
            <div className="h-72 sm:h-96">
              <MapContainer 
                center={[lastPosition.lat, lastPosition.lng]} 
                zoom={15} 
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; OpenStreetMap'
                />
                
                {/* Path */}
                {tripData.locations && tripData.locations.length > 1 && (
                  <Polyline 
                    positions={tripData.locations.map(p => [p.lat, p.lng])}
                    color={tripData.status === "sos" ? "#FF3B30" : "#FF3366"}
                    weight={4}
                    opacity={0.8}
                  />
                )}
                
                {/* Current/Last position marker */}
                <Marker 
                  position={[lastPosition.lat, lastPosition.lng]}
                  icon={tripData.status === "sos" ? sosIcon : userIcon}
                >
                  <Popup>
                    <strong>{tripData.user_name}</strong><br/>
                    {tripData.status === "sos" ? "Position de l'alerte SOS" : "Dernière position connue"}<br/>
                    <small>{new Date(lastPosition.timestamp).toLocaleTimeString('fr-FR')}</small>
                  </Popup>
                </Marker>
                
                {tripData.locations && tripData.locations.length > 1 && (
                  <FitBounds locations={tripData.locations} />
                )}
              </MapContainer>
            </div>
          </Card>
        )}

        {/* Trip Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin size={20} className="text-[#FF3366]" />
              Détails du trajet
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
              <MapPin className="text-[#FF3366] mt-0.5" size={20} />
              <div>
                <p className="font-medium text-slate-900">Destination</p>
                <p className="text-slate-600">{tripData?.destination}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
              <Clock className="text-[#FF3366] mt-0.5" size={20} />
              <div>
                <p className="font-medium text-slate-900">Temps</p>
                <p className="text-slate-600">{timeInfo?.elapsed}</p>
                {timeInfo?.remaining && (
                  <p className={`text-sm ${timeInfo.remaining.includes('dépassé') ? 'text-amber-600' : 'text-green-600'}`}>
                    {timeInfo.remaining}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
              <User className="text-[#FF3366] mt-0.5" size={20} />
              <div>
                <p className="font-medium text-slate-900">Utilisateur</p>
                <p className="text-slate-600">{tripData?.user_name}</p>
              </div>
            </div>
            
            {tripData?.message && (
              <div className="p-3 bg-[#FF3366]/5 rounded-xl border border-[#FF3366]/20">
                <p className="text-sm text-slate-600 italic">"{tripData.message}"</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Privacy Notice */}
        <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
          <p className="text-xs text-blue-700 text-center">
            🔒 Les données de localisation ne sont pas conservées au-delà de 24h.
          </p>
        </div>

        {/* Last update info */}
        <p className="text-center text-sm text-slate-400">
          Dernière mise à jour : {lastUpdate?.toLocaleTimeString('fr-FR')}
          {tripData?.status === "active" && " • Actualisation auto toutes les 30s"}
        </p>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-pink-100 py-6 mt-8">
        <div className="container mx-auto px-4 text-center text-slate-500 text-sm">
          <p>© 2026 RATE⭐SHARE - Le GPS de la Bienveillance</p>
          <p className="mt-1">Safe Check - Partagez votre trajet en sécurité</p>
        </div>
      </footer>
    </div>
  );
};

export default TrackingPage;
